import { getCompleteSizes } from '@cian/adfox-component';
import * as React from 'react';
import { createRoot, Root } from 'react-dom/client';
import { useSelector } from 'react-redux';

import { AdfoxBanner } from 'shared/components/AdfoxBanner';
import { ApplicationContext, useApplicationContext } from 'shared/containers/ApplicationContext';
import { TAdfoxContextData, AdfoxContext, TAdfoxContextProviderProps } from 'shared/context/AdfoxContext';
import { useAdfoxParams } from 'shared/context/AdfoxContext/internal/hooks/useAdfoxPuids';
import { selectIsBookedFromDeveloper } from 'shared/selectors/newbuildingBookedFromDeveloper';
import { selectIsNewbuildingPremiumRate } from 'shared/selectors/newbuildingPremiumRate';

const POST_VIEW_ANALYTICS_DATA: TAdfoxContextProviderProps['postViewAnalyticsData'] = {
  place: 'above_header',
  place__DEPRECATED: 'offerCard',
  name__DEPRECATED: 'StretchBanner',
};
const P_PARAMS: TAdfoxContextProviderProps['pParams'] = { p1: 'bvknz', p2: 'y' };
const SIZE: TAdfoxContextProviderProps['size'] = { width: '100%', height: 200 };

export const StretchBannerContainer: React.FC = () => {
  const stretchBannerRoot = React.useRef<Root | null>(null);
  const elementRef = React.useRef<HTMLElement | null>(null);

  const context = useApplicationContext();

  const params = useAdfoxParams(P_PARAMS);

  const isPremiumNewbuilding = useSelector(selectIsNewbuildingPremiumRate);
  const isBookedFromDeveloper = useSelector(selectIsBookedFromDeveloper);

  const adfoxContextProviderValue = React.useMemo(
    (): TAdfoxContextData => ({
      postViewAnalyticsData: POST_VIEW_ANALYTICS_DATA,
      sizes: getCompleteSizes(SIZE),
      params,
      isHidden: isPremiumNewbuilding || isBookedFromDeveloper,
    }),
    [isPremiumNewbuilding, isBookedFromDeveloper, params],
  );

  React.useEffect(() => {
    /* istanbul ignore next */
    if (!stretchBannerRoot.current) {
      const el = document.getElementById('adfox-stretch-banner');

      /* istanbul ignore next */
      if (el) {
        elementRef.current = el;
        stretchBannerRoot.current = createRoot(el);

        stretchBannerRoot.current.render(
          <ApplicationContext.Provider value={context}>
            <AdfoxContext.Provider value={adfoxContextProviderValue}>
              <AdfoxBanner />
            </AdfoxContext.Provider>
          </ApplicationContext.Provider>,
        );
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return null;
};
